/**
 * Bridge Types
 * 定義 Bridge 相關的資料結構和介面
 */

/**
 * Bridge Action 列舉
 * 定義所有可用的 Bridge 動作，避免拼寫錯誤
 */
export enum BridgeAction {
  // Spinner 相關
  GET_STUDENT_LIST = 'getStudentList',
  STUDENT_PICKED = 'studentPicked',
  STUDENT_REMOVED = 'studentRemoved',

  // Game Leaderboard 相關
  GET_ACCESS_TOKEN = 'getAccessToken',
  GET_CURRENT_GAME_INFO = 'getCurrentGameInfo',
  SEND_END_GAME = 'sendEndGame',
}

/**
 * 學生資料結構
 *
 * Invariant: studentId 與 serialNumber MUST NOT 同時為 null。
 * 新版 host：serialNumber 為 number；studentId 可能為 null（未進教室）。
 * 舊版 Windows host：serialNumber 為 null；studentId 必為非空 string。
 */
export interface Student {
  studentId: string | null
  name: string
  seatNumber: string | null
  serialNumber: number | null
}

/**
 * Bridge 事件定義
 *
 * 與 Student 同樣的 invariant：兩個識別欄位不會同時為 null。
 */
export interface StudentPickedEvent {
  studentId: string | null
  serialNumber: number | null
}

export interface StudentRemovedEvent {
  studentId: string | null
  serialNumber: number | null
}

/**
 * Game Leaderboard 相關類型定義
 */

/**
 * 遊戲資訊
 */
export interface GameInfo {
  gameId: string
  title: string
  lessonId: string
  isEndGame: boolean
}

interface StudentRanking {
  studentId: string
  studentName: string
  score: number
}

/**
 * 結束遊戲事件
 */
export interface EndGameEvent {
  ranking: StudentRanking[]
}

/**
 * Bridge 訊息格式
 */
export interface BridgeMessage {
  event: string
  data: unknown
  timestamp: string
  messageId?: string
}

/**
 * Bridge 回應格式
 */
export interface BridgeResponse<T = unknown> {
  success: boolean
  data?: T
  error?: string
  timestamp: string
}

/**
 * 學生列表回應
 */
export interface StudentListResponse {
  data: Student[]
}

/**
 * Bridge 連接狀態
 */
export type ConnectionStatus =
  | 'connected'
  | 'disconnected'
  | 'connecting'
  | 'error'

/**
 * Bridge 連接資訊
 */
export interface ConnectionInfo {
  status: ConnectionStatus
  platform: string
  lastActivity?: string
  error?: string
}

/**
 * Bridge API 介面
 */
export interface BridgeAPI {
  // 連接管理
  connect(): Promise<void>
  disconnect(): void
  getConnectionInfo(): ConnectionInfo
  isConnected(): boolean

  // Spinner 相關 API
  getStudentList(): Promise<Student[]>
  studentPicked(event: StudentPickedEvent): Promise<boolean>
  studentRemoved(event: StudentRemovedEvent): Promise<boolean>

  // Game Leaderboard 相關 API
  getAccessToken(): Promise<string>
  getCurrentGameInfo(): Promise<GameInfo>
  sendEndGame(event?: EndGameEvent): Promise<void>

  // 事件管理
  on(event: string, callback: (data: unknown) => void): () => void
  off(event: string, callback?: (data: unknown) => void): void

  // 平台資訊
  getPlatform(): string
  getPlatformInfo(): unknown
}
